// PDFDocument.js
import { Document, Page, Text, Image, StyleSheet } from "@react-pdf/renderer";

// Styles
const styles = StyleSheet.create({
  page: {
    padding: 25,
    fontFamily: "Helvetica",
    lineHeight: 1.5,
  },
  title: {
    fontSize: 18,
    textAlign: "center",
    marginBottom: 16,
    fontWeight: "bold",
  },
   heading: {
    fontSize: 16,
    marginBottom: 6,
    fontWeight: "bold",
    color: "#DD313A",
  },
  paragraph: {
    fontSize: 12,
    marginBottom: 6,
    textAlign: "justify",
  },
  strong: {
    fontWeight: "bold",
  },
  italic: {
    fontStyle: "italic",
  },
  bullet: {
   fontSize: 12,
    marginLeft: 12,
    marginBottom: 4,
},
  image: {
    width: 150,
    height: "auto",
    marginVertical: 10,
    objectFit: "contain",
  },

  figureImage: {
    width: "100%",
    height: "100%",
    // marginVertical: 10,
    objectFit: "fill",
  },
  
});

// Inline style parser
const parseInlineStyle = (html) => {
  const styleMatch = html.match(/style="([^"]+)"/i);
  if (!styleMatch) return {};

  const styleStr = styleMatch[1];
  const styleObj = {};

  styleStr.split(";").forEach((item) => {
    const [key, value] = item.split(":").map(s => s.trim());
    if (!key || !value) return;
    // map CSS property to react-pdf style
    if (key === "color") styleObj.color = value;
    if (key === "font-weight" && value === "bold") styleObj.fontWeight = "bold";   
    if (key === "font-style" && value === "italic") styleObj.fontStyle = "italic";
  });

  return styleObj;
};


// Helper function to parse HTML manually
const parseHtmlToPdfElements = (html) => {
  const blocks = html
    // .split(/<\/p>|<\/figure>/gi)
    // .map((block) => block.replace(/<p[^>]*>/gi, "").trim())
    // .filter(Boolean);

    .split(/<\/p>|<\/li>|<\/h[1-6]>|<\/ul>|<\/ol>/gi)
    .map((block) =>
      block
        // Remove only tags that are useless for PDF
        .replace(/<\/?p[^>]*>/gi, "\n")
        .replace(/<li[^>]*>/gi, "• ") // <-- bullet add karo
      .replace(/<ul[^>]*>|<ol[^>]*>/gi, "") // remove UL/OL tags
      .replace(/<\/?strong[^>]*>/gi, "")
      .replace(/<\/?span[^>]*>/gi, "")
        .trim()
    )
    .filter(Boolean);

  return blocks.map((block, index) => {
   // Figure image
    const figureImgMatch = block.match(/<figure[^>]*>.*<img[^>]+src=["']([^"']+)["'][^>]*>.*<\/figure>/i);
    if (figureImgMatch) {
      const src = figureImgMatch[1];
      return (
        <Image
          key={`figure-img-${index}`}
          src={src}
          style={styles.figureImage}
        />
      );
    }

    // Normal img tag
    const imgMatch = block.match(/<img[^>]+src=["']([^"']+)["'][^>]*>/i);
    if (imgMatch) {
      const src = imgMatch[1];
      return (
        <Image
          key={`img-${index}`}
          src={src}
          style={styles.image}
        />
      );
    }

    if (block.match(/^<h[1-6]/i)) {
      const text = block.replace(/<\/?h[1-6][^>]*>/gi, "");
      const inlineStyle = parseInlineStyle(block);
      return <Text key={index} style={[styles.heading, inlineStyle]}>{text}</Text>;
    }

    // Handle <strong>
    if (block.match(/<strong>/i)) {
      const text = block.replace(/<\/?strong>/gi, "");
      const inlineStyle = parseInlineStyle(block);
      return (
        <Text key={index} style={[styles.paragraph, styles.strong, inlineStyle]}>
          {text}
        </Text>
      );
    }

    // Handle <i>
    if (block.match(/<i>/i)) {
      const text = block.replace(/<\/?i>/gi, "");
      const inlineStyle = parseInlineStyle(block);
      return (
        <Text key={index} style={[styles.paragraph, styles.italic, inlineStyle]}>
          {text}
        </Text>
      );
    }

    // Handle bullet points (lines starting with • or o)
    if (block.startsWith("•") || block.startsWith("o")) {
      const inlineStyle = parseInlineStyle(block);
      return (
        <Text key={index} style={[styles.bullet, inlineStyle]}>
          {block}
        </Text>
      );
    }

    if (block.match(/<p>/i)) {
      const inlineStyle = parseInlineStyle(block);
      return (
        <Text key={index} style={[styles.paragraph, inlineStyle]}>
        {block}
      </Text>
      );
    }

    // Default paragraph
    return (
      <Text key={index} style={styles.paragraph}>
        {block}
      </Text>
    );
  });
};

// Main PDF component
const PDFDocument = ({ title, content }) => {
  // Unescape escaped HTML from CKEditor
  const unescapeHtml = (str) => {
    if (!str) return "";
    return str
      .replace(/\\"/g, '"')
      .replace(/\\n/g, "")
      .replace(/\\t/g, "")
      .replace(/\\r/g, "")
      .replace(/\\\//g, "/")
      .replace(/&nbsp;/g, " ");
  };

  const safeHtml = unescapeHtml(content);

  // 2️⃣ Parse for PDF
  const parsedElements = parseHtmlToPdfElements(safeHtml);

  // 3️⃣ Return PDF Document
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>{title}</Text>
        {parsedElements}
      </Page>
    </Document>
  );
};


export default PDFDocument;
